import { type MutableRefObject, useEffect } from 'react';

const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [role="combobox"], [role="listbox"], [contenteditable="true"]';
const ACTIVATABLE_SELECTOR = `${TEXT_ENTRY_SELECTOR}, button, a[href]`;

const targetMatches = (target: EventTarget | null, selector: string): boolean =>
  target instanceof Element && Boolean(target.closest(selector));

interface ReviewShortcutOptions {
  enabled: boolean;
  isBlocked?: boolean;
  isContinueMode?: boolean;
  canDecide?: boolean;
  pauseNavigationRef?: MutableRefObject<boolean>;
  onApprove: () => void;
  onReject: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

export const useReviewKeyboardShortcuts = ({
  enabled,
  isBlocked = false,
  isContinueMode = false,
  canDecide = true,
  pauseNavigationRef,
  onApprove,
  onReject,
  onNext,
  onPrevious,
}: ReviewShortcutOptions) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || isBlocked) return;
      if (!e.altKey && targetMatches(e.target, e.key === 'Enter' ? ACTIVATABLE_SELECTOR : TEXT_ENTRY_SELECTOR)) return;
      if (isContinueMode) {
        if (e.key === 'ArrowRight' || e.key === 'Enter') {
          e.preventDefault();
          onNext();
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onPrevious();
        }
        return;
      }
      if (e.altKey) {
        if (!canDecide) return;
        if (e.code === 'KeyA') {
          e.preventDefault();
          onApprove();
        }
        if (e.code === 'KeyR') {
          e.preventDefault();
          onReject();
        }
        return;
      }
      if (pauseNavigationRef?.current) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onPrevious();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canDecide, enabled, isBlocked, isContinueMode, onApprove, onNext, onPrevious, onReject, pauseNavigationRef]);
};
